import { IndexName } from '@enums/IndexName';
import { Country } from '@models/CountriesAndLanguages';
import { ChannelQueryParams, GetAllChannelsDTO, Schedule, TvChannelSchedule } from '@models/Epg';
import FilterDefinition from '@models/FilterDefinition';
import QueryLog from '@models/QueryLogs';
import { FilterGroupWithoutId } from '@models/SearchFilter';
import { SearchQueryDTO } from '@models/SearchQuery';
import SearchResults from '@models/SearchResults';
import { StatisticsDTO } from '@models/Statistics';
import { DetailedTitle, PlaybackOptionDetailed, TitleType } from '@models/Title';
import { SimpleTitle } from '@models/Title';
import { SourceId } from '@modules/simulations/types';
import { ExtendedTitleContext } from '@store/titles/types';
import { DEFAULT_CHUNK_SIZE__EPISODES } from '@utils/chunkSize';
import { removeEmptyParams, removeFilterIds, sanitize } from '@utils/filterUtils';

import { ApiContext } from '../shared/types';
import { createAdminApi, getApiBaseUrl } from '../shared/utils';

type SearchQueryParamsWithoutId = Omit<SearchQueryDTO, 'filter'> & {
  filter?: FilterGroupWithoutId;
};

export const removeIdsFromSearchParams = (params: SearchQueryDTO): SearchQueryParamsWithoutId => {
  const transformedParams = params.filter
    ? { ...params, filter: removeFilterIds(params.filter) }
    : (params as SearchQueryParamsWithoutId);

  return removeEmptyParams(transformedParams);
};

const api = createAdminApi(getApiBaseUrl('titles'), { 'x-api-version': '2.0' });

export const fetchIndexes = () => api.get<string[]>('indexes');

export const fetchTitle =
  ({ atsToken, isNewIngest }: ApiContext) =>
  ({ masterId, platformTitleId, providerIdentifier, subscriberId }: ExtendedTitleContext) => {
    let url = '';
    if (masterId) url = masterId;
    else if (platformTitleId) url = `byPlatformTitleId/${platformTitleId}`;

    return api.get<DetailedTitle>(
      url,
      {
        providerIdentifier,
        includeExpired: true,
        ...(masterId && isNewIngest ? { isNewIngest: true } : {}),
      },
      {
        headers: { ...(subscriberId && atsToken ? { Authorization: `AtsToken ${atsToken}` } : {}) },
      }
    );
  };

export const fetchPlaybackOption =
  ({ atsToken, isNewIngest }: ApiContext) => (playbackOptionId: string, subscriberId?: string) =>
    api.get<PlaybackOptionDetailed>(
      `playbackOptions/${playbackOptionId}`,
      isNewIngest ? { isNewIngest: true } : undefined,
      {
        headers: { ...(subscriberId && atsToken ? { Authorization: `AtsToken ${atsToken}` } : {}) },
      }
    );

export const fetchTitles = (masterIds: string[]) =>
  api.get<SearchResults<SimpleTitle>>('search', { id: masterIds.join(',') });

export const fetchEpisodes = (
  seriesMasterId: string,
  source?: string,
  pageNo?: number,
  isNewIngest?: boolean
) => {
  let skipTake;
  if (pageNo != null)
    skipTake = {
      skip: pageNo ? DEFAULT_CHUNK_SIZE__EPISODES * pageNo : undefined,
      take: DEFAULT_CHUNK_SIZE__EPISODES,
    };

  return api.get<DetailedTitle[]>(`series/${seriesMasterId}/episodes`, {
    source,
    ...skipTake,
    ...(isNewIngest ? { isNewIngest: true } : {}),
  });
};

export const fetchSuggestedValues =
  ({ indexName }: ApiContext) =>
  /**
   * @param queryText Search text
   * @param fieldName Corresponds to filter field; leave empty to suggest title values
   * @returns List of suggested values
   */
  (queryText: string, fieldName?: string) =>
    api.get<string[]>(`${indexName}/suggest`, sanitize({ queryText, fields: fieldName }));

export const fetchFilterDefs = (indexName: IndexName) =>
  api.get<FilterDefinition[]>(`${indexName}/filters`);

export const fetchTitleCountsByProvider = (type: TitleType, sourceId?: SourceId) => {
  const filter = [`Type eq '${type}'`, ...(sourceId ? [`SourceId eq '${sourceId}'`] : [])].join(
    ' and '
  );

  return api.get<StatisticsDTO[]>('titles/countperprovider', { $filter: filter });
};

export const searchTitles =
  ({ indexName, isNewIngest }: ApiContext) =>
  (params: SearchQueryDTO, contextIndexName?: IndexName) =>
    api.post<SearchResults<SimpleTitle>>(
      'search',
      removeIdsFromSearchParams({
        ...params,
        indexName: contextIndexName ?? indexName,
        ...(isNewIngest ? { isNewIngest: true } : {}),
      })
    );

// EPG
export const fetchAllChannels = (country?: Country) =>
  api.get<GetAllChannelsDTO>(
    'channels',
    { take: 1000 },
    { headers: { ...(country ? { country } : {}) } }
  );

// export const fetchTitlesForChannelAtASpecificTime = (
//   channelId: string,
//   params?: ChannelQueryParams
// ) => api.get<unknown[]>(`channels/${channelId}`, params);

export const fetchChannelScheduleToday = (channelId: string, params?: ChannelQueryParams) =>
  api.get<TvChannelSchedule>(`channels/${channelId}/schedule/today`, params);

export const fetchScheduleToday = (params?: ChannelQueryParams) =>
  api.get<Schedule>('schedule/today', params);

export const fetchQueryLogs = (params?: ChannelQueryParams) =>
  api.get<QueryLog[]>('querylogs', params);
